import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { MapPin, Mail, Phone, Send, Radio, Newspaper } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ConnectWithUs = () => {
  const [submitting, setSubmitting] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    department: '',
    subject: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.department) {
      toast.error('Please select a department.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, contactForm);
      toast.success(res.data.message);
      setContactForm({
        name: '',
        email: '',
        phone: '',
        department: '',
        subject: '',
        message: ''
      });
    } catch (err) {
      toast.error('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewsletterSubscribe = async () => {
    if (!newsletterEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newsletterEmail)) {
      toast.error('Please enter a valid email address.');
      return;
    }
    setSubscribing(true);
    try {
      const res = await axios.post(`${API}/newsletter`, { email: newsletterEmail });
      toast.success(res.data.message);
      setNewsletterEmail('');
    } catch (err) {
      toast.error('Subscription failed. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  const offices = [
    {
      name: 'Harare HQ',
      address: '6 Everett Close, Mount Pleasant, Harare, Zimbabwe',
      coordinates: [-17.7833, 31.0500],
      type: 'headquarters',
      image: 'https://images.unsplash.com/photo-1524594081293-190a2fe0baae?q=85&w=800'
    },
    {
      name: 'Johannesburg Office',
      address: 'Sandton, Johannesburg, South Africa',
      coordinates: [-26.1076, 28.0567],
      type: 'regional',
      image: 'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?q=85&w=800'
    },
    {
      name: 'Nairobi Office',
      address: 'Westlands, Nairobi, Kenya',
      coordinates: [-1.2675, 36.8070],
      type: 'regional',
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=85&w=800'
    },
    {
      name: 'Dubai Office',
      address: 'DIFC, Dubai, UAE',
      coordinates: [25.2048, 55.2708],
      type: 'regional',
      image: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?q=85&w=800'
    }
  ];

  const contactDepartments = [
    {
      department: 'Investors & Strategy',
      email: 'strategy@xsi.co.zw',
      specialization: 'Project Funding & Strategic Partnerships',
      icon: <Mail className="w-6 h-6" />,
      color: '#00CED1'
    },
    {
      department: 'Media Relations',
      email: 'info@xsi.co.zw',
      specialization: 'Press Inquiries & #XcelsiorRises',
      icon: <Newspaper className="w-6 h-6" />,
      color: '#FF8C00'
    },
    {
      department: 'Commodities',
      email: 'trade@xsi.co.zw',
      specialization: 'Mineral & Agri Exports',
      icon: <Mail className="w-6 h-6" />,
      color: '#00CED1'
    },
    {
      department: 'Careers',
      email: 'talent@xsi.co.zw',
      specialization: 'Join Our Team',
      icon: <Mail className="w-6 h-6" />,
      color: '#FF8C00'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1761477103792-fc535e837d7b?crop=entropy&cs=srgb&fm=jpg&q=85')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-[#00CED1]/60 to-transparent"></div>
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: 'linear-gradient(45deg, rgba(255, 140, 0, 0.1) 25%, transparent 25%, transparent 75%, rgba(255, 140, 0, 0.1) 75%), linear-gradient(45deg, rgba(255, 140, 0, 0.1) 25%, transparent 25%, transparent 75%, rgba(255, 140, 0, 0.1) 75%)',
            backgroundSize: '60px 60px',
            backgroundPosition: '0 0, 30px 30px'
          }}></div>
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-6 text-white text-center">
          <h1 className="text-6xl font-bold mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Connect With Us
          </h1>
          <p className="text-2xl text-gray-200">Let's Build Africa's Future Together</p>
        </div>
      </section>

      {/* Global Presence Map */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <MapPin className="w-16 h-16 text-[#00CED1] mx-auto mb-4" />
            <h2 className="text-5xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Our Global Presence
            </h2>
            <div className="w-24 h-1 bg-[#00CED1] mx-auto mb-8"></div>
          </div>

          {/* Map Placeholder */}
          <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg h-[500px] mb-12 flex items-center justify-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-[#00CED1]/10 to-[#FF8C00]/10"></div>
            <div className="absolute inset-0 opacity-20" style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1644088379091-d574269d422f?q=85&w=800')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}></div>
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(0, 206, 209, 0.1) 0%, transparent 70%)',
            }}></div>
            <div className="relative z-10 text-center">
              <MapPin className="w-24 h-24 text-[#00CED1] mx-auto mb-4" />
              <p className="text-2xl font-bold text-gray-800 mb-2">Interactive Map</p>
              <p className="text-gray-600">Operating across Africa, Middle East & Global Markets</p>
            </div>
          </div>

          {/* Office Locations */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {offices.map((office, index) => (
              <Card 
                key={index} 
                className={`${
                  office.type === 'headquarters' 
                    ? 'border-2 border-[#FF8C00] shadow-xl' 
                    : 'border border-gray-200'
                } hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden`}
              >
                <div className="relative h-36 overflow-hidden">
                  <img src={office.image} alt={office.name} className="w-full h-full object-cover transition-transform duration-300 hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                </div>
                <CardContent className="p-6 text-center">
                  <div className={`${
                    office.type === 'headquarters' 
                      ? 'w-4 h-4 bg-[#FF8C00] animate-pulse' 
                      : 'w-3 h-3 bg-[#00CED1]'
                  } rounded-full mx-auto mb-4`}></div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{office.name}</h3>
                  {office.type === 'headquarters' && (
                    <div className="text-xs font-semibold text-[#FF8C00] mb-2">HEADQUARTERS</div>
                  )}
                  <p className="text-sm text-gray-600">{office.address}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Matrix */}
      <section className="py-20 px-6 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Department Contacts
            </h2>
            <div className="w-24 h-1 bg-[#FF8C00] mx-auto mb-8"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {contactDepartments.map((dept, index) => (
              <Card 
                key={index} 
                className="hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="h-2" style={{ backgroundColor: dept.color }}></div>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div 
                      className="p-3 rounded-full" 
                      style={{ backgroundColor: `${dept.color}20`, color: dept.color }}
                    >
                      {dept.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{dept.department}</h3>
                      <a 
                        href={`mailto:${dept.email}`}
                        className="text-[#00CED1] hover:text-[#00b8bb] font-semibold mb-2 block transition-colors"
                      >
                        {dept.email}
                      </a>
                      <p className="text-sm text-gray-600">{dept.specialization}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Live Message Hub */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <Send className="w-16 h-16 text-[#00CED1] mx-auto mb-4" />
            <h2 className="text-5xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Send Us a Message
            </h2>
            <div className="w-24 h-1 bg-[#00CED1] mx-auto mb-8"></div>
            <p className="text-xl text-gray-600">We respond within 24 hours</p>
          </div>

          <Card className="border-2 border-[#00CED1]">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                      required
                      placeholder="Your name"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                      required
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                      placeholder="+263..."
                    />
                  </div>

                  <div>
                    <Label htmlFor="department">Department *</Label>
                    <Select 
                      value={contactForm.department} 
                      onValueChange={(value) => setContactForm({...contactForm, department: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select department" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Investors">Investors & Strategy</SelectItem>
                        <SelectItem value="Media">Media Relations</SelectItem>
                        <SelectItem value="Commodities">Commodities</SelectItem>
                        <SelectItem value="Careers">Careers</SelectItem>
                        <SelectItem value="General">General Inquiry</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    id="subject"
                    value={contactForm.subject}
                    onChange={(e) => setContactForm({...contactForm, subject: e.target.value})}
                    required
                    placeholder="What is this about?"
                  />
                </div>

                <div>
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    value={contactForm.message}
                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                    required
                    rows={6}
                    placeholder="Your message..."
                  />
                </div>

                <Button 
                  type="submit" 
                  size="lg" 
                  disabled={submitting}
                  data-testid="contact-submit-button"
                  className="w-full bg-[#FF8C00] hover:bg-[#e07d00] text-white font-bold text-lg py-6 transition-all duration-300 transform hover:scale-105"
                >
                  <Send className="w-5 h-5 mr-2" />
                  {submitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Social Integration */}
      <section className="py-20 px-6 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Radio className="w-16 h-16 text-[#00CED1] mx-auto mb-4" />
            <h2 className="text-5xl font-bold mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Stay Connected
            </h2>
            <div className="w-24 h-1 bg-[#00CED1] mx-auto mb-8"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="bg-white/10 border-gray-700">
              <CardContent className="p-8 text-center">
                <Newspaper className="w-12 h-12 text-[#00CED1] mx-auto mb-4" />
                <h3 className="text-2xl font-bold mb-4">Newsletter</h3>
                <p className="text-gray-300 mb-6">Get Market Intelligence & Updates</p>
                <div className="flex gap-2">
                  <Input 
                    placeholder="your@email.com" 
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    data-testid="newsletter-email-input"
                    className="bg-white/10 border-gray-600 text-white"
                  />
                  <Button 
                    onClick={handleNewsletterSubscribe}
                    disabled={subscribing}
                    data-testid="newsletter-subscribe-button"
                    className="bg-[#FF8C00] hover:bg-[#e07d00] text-white"
                  >
                    {subscribing ? '...' : 'Subscribe'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/10 border-gray-700">
              <CardContent className="p-8 text-center">
                <Radio className="w-12 h-12 text-[#FF8C00] mx-auto mb-4" />
                <h3 className="text-2xl font-bold mb-4">Podcast</h3>
                <p className="text-gray-300 mb-6">Frontiers of African Sustainability</p>
                <Button className="bg-[#00CED1] hover:bg-[#00b8bb] text-white">
                  Listen Now
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="mt-12 text-center">
            <h3 className="text-2xl font-bold mb-4">#XcelsiorRises</h3>
            <p className="text-gray-300">Follow our journey on social media</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ConnectWithUs;